// storage-admin.repo — Kelola Penyimpanan: daftar objek lintas bucket + status referensi + hapus-orphan.
// KUNCI (koreksi reviewer): objek yang MASIH tertaut record (bukti hukum kuasa / bukti hadir selfie /
// bukti beban) DILINDUNGI — hanya ORPHAN (sisa foto diganti / tak tertaut) yang boleh dihapus.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { listAllFiles, type StoredObject } from '@core/storage';

export interface StoredFile extends StoredObject { referenced: boolean }
export interface BucketOverview { bucket: string; label: string; files: StoredFile[]; totalSize: number; orphanCount: number }

/** Set path yang MASIH direferensikan (dilindungi dari hapus). */
async function referencedPaths(bucket: string): Promise<Set<string>> {
  const s = new Set<string>();
  if (bucket === 'bukti') {
    const data = await fetchAllPaged<{ bukti: string | null }>((a, b) =>
      supabase().from('expenses').select('bukti').not('bukti', 'is', null).order('id').range(a, b));
    data.forEach((r) => { if (r.bukti) s.add(r.bukti); });
  } else if (bucket === 'unit') {
    // Foto kondisi unit: sesi berjalan (tickets.foto_unit) DAN sesi lama yang terarsip ke
    // ticket_rounds.data saat servis-ulang — keduanya bukti, keduanya dilindungi.
    const data = await fetchAllPaged<{ foto_unit: { path?: string }[] | null }>((a, b) =>
      supabase().from('tickets').select('foto_unit').neq('foto_unit', '[]').order('no').range(a, b));
    data.forEach((r) => (r.foto_unit ?? []).forEach((f) => { if (f?.path) s.add(f.path); }));
    const rd = await fetchAllPaged<{ data: { foto_unit?: { path?: string }[] } | null }>((a, b) =>
      supabase().from('ticket_rounds').select('data').not('data', 'is', null).order('id').range(a, b));
    rd.forEach((r) => (r.data?.foto_unit ?? []).forEach((f) => { if (f?.path) s.add(f.path); }));
  } else if (bucket === 'kuasa') {
    // Ambil HANYA `.foto` — kolom `diwakilkan` juga memuat tanda tangan base64 yang tak dipakai di sini.
    const data = await fetchAllPaged<{ foto: string | null }>((a, b) =>
      supabase().from('tickets').select('diwakilkan->>foto').not('diwakilkan', 'is', null).order('no').range(a, b));
    data.forEach((r) => { if (r.foto) s.add(r.foto); });
    // Foto kuasa sesi lama (servis-ulang) diarsipkan ke ticket_rounds.data.diwakilkan.foto → tetap DILINDUNGI.
    const rd = await fetchAllPaged<{ data: { diwakilkan?: { foto?: string | null } } | null }>((a, b) =>
      supabase().from('ticket_rounds').select('data').not('data', 'is', null).order('id').range(a, b));
    rd.forEach((r) => { const f = r.data?.diwakilkan?.foto; if (f) s.add(f); });
  } else if (bucket === 'absensi') {
    // Selfie clock-in + foto request-koreksi (korr_log[].foto) — keduanya bukti hadir/HR → DILINDUNGI.
    const data = await fetchAllPaged<{ selfie: string | null; korr_log: { foto?: string | null }[] | null }>((a, b) =>
      supabase().from('attendance').select('selfie,korr_log').or('selfie.not.is.null,korr_log.not.is.null').order('id').range(a, b));
    data.forEach((r) => {
      if (r.selfie) s.add(r.selfie);
      (r.korr_log ?? []).forEach((e) => { if (e.foto) s.add(e.foto); });
    });
  }
  return s;
}

export async function bucketOverview(bucket: string, label: string): Promise<BucketOverview> {
  const [files, refs] = await Promise.all([listAllFiles(bucket), referencedPaths(bucket)]);
  const marked: StoredFile[] = files.map((f) => ({ ...f, referenced: refs.has(f.path) }));
  return { bucket, label, files: marked, totalSize: files.reduce((a, f) => a + f.size, 0), orphanCount: marked.filter((f) => !f.referenced).length };
}

/** Hapus objek — HANYA bila orphan. Re-cek referensi saat hapus (cegah TOCTOU hapus bukti). */
export async function deleteIfOrphan(bucket: string, path: string): Promise<void> {
  const refs = await referencedPaths(bucket);
  if (refs.has(path)) throw new Error('Foto ini masih tertaut record (bukti hukum/hadir) — dilindungi, tak bisa dihapus.');
  const { error } = await supabase().storage.from(bucket).remove([path]);
  if (error) throw new Error(error.message);
}

// ── Peta tautan per-path: nomor/label yang bisa DIKLIK + nama file unduhan yang rapi ──
export interface RefInfo {
  ticketNo?: string;      // bisa diklik (tno-link → detail tiket)
  label: string;          // teks tautan utk non-tiket (beban/absensi)
  /** Baris kedua: keterangan siapa/apa — pelanggan & unit, pemohon & pemutus approval, dst. */
  detail?: string;
  /** id beban → tombol buka rincian bebannya (bukti beban tak punya nomor tiket). */
  expenseId?: number;
  dlName: string;         // nama file unduhan: "<no>-<pelanggan>.jpg" dst.
}

const bersih = (s: string): string => s.replace(/[^\w\-]+/g, '_').replace(/_+/g, '_').slice(0, 60);

/** Path → info tautan (sekali fetch per bucket — dipakai kolom "Tertaut ke" + rename unduhan). */
export async function referenceInfo(bucket: string): Promise<Map<string, RefInfo>> {
  const m = new Map<string, RefInfo>();
  if (bucket === 'unit') {
    const data = await fetchAllPaged<unknown>((a, b) => supabase().from('tickets')
      .select('no,merk,model,foto_unit,customers(nama)').neq('foto_unit', '[]').order('no').range(a, b));
    (data as { no: string; merk: string | null; model: string | null; foto_unit: { path?: string; fase?: string }[] | null; customers: { nama: string } | { nama: string }[] | null }[]).forEach((r) => {
      const c = Array.isArray(r.customers) ? r.customers[0] : r.customers;
      const nm = c?.nama ?? 'pelanggan';
      const unit = [r.merk, r.model].filter(Boolean).join(' ');
      (r.foto_unit ?? []).forEach((f, i) => {
        if (!f?.path) return;
        m.set(f.path, { ticketNo: r.no, label: r.no, detail: `${nm}${unit ? ` · ${unit}` : ''} · ${f.fase === 'serah' ? 'saat diserahkan' : 'saat diterima'}`, dlName: `${r.no}-unit${i + 1}.jpg` });
      });
    });
  } else if (bucket === 'kuasa') {
    // Nama pelanggan + unit ikut ditampilkan: saat menelusuri bukti hukum, "PKP-260719-P8EJ"
    // saja tak memberi tahu ini surat kuasa siapa dan HP apa.
    const data = await fetchAllPaged<unknown>((a, b) =>
      supabase().from('tickets').select('no,merk,model,diwakilkan->>foto,customers(nama)').not('diwakilkan', 'is', null).order('no').range(a, b));
    (data as { no: string; merk: string | null; model: string | null; foto: string | null; customers: { nama: string } | { nama: string }[] | null }[]).forEach((r) => {
      const f = r.foto; if (!f) return;
      const c = Array.isArray(r.customers) ? r.customers[0] : r.customers;
      const nm = c?.nama ?? 'pelanggan';
      const unit = [r.merk, r.model].filter(Boolean).join(' ');
      m.set(f, { ticketNo: r.no, label: r.no, detail: `${nm}${unit ? ` · ${unit}` : ''}`, dlName: `${r.no}-${bersih(nm)}.jpg` });
    });
    const rd = await fetchAllPaged<{ ticket_no: string; ronde: number | null; data: { diwakilkan?: { foto?: string | null } } | null }>((a, b) =>
      supabase().from('ticket_rounds').select('ticket_no,ronde,data').not('data', 'is', null).order('id').range(a, b));
    rd.forEach((r) => {
      const f = r.data?.diwakilkan?.foto; if (!f) return;
      m.set(f, { ticketNo: r.ticket_no, label: `${r.ticket_no} (sesi ${r.ronde ?? '?'})`, detail: 'surat kuasa sesi lama', dlName: `${r.ticket_no}-sesi${r.ronde ?? 0}.jpg` });
    });
  } else if (bucket === 'bukti') {
    // Nomor dokumen (BBN-…) jadi tautan → rincian bebannya. Id angka internal tak berguna
    // saat mencocokkan dengan kuitansi fisik.
    // no_dok baru ada setelah migrasi 0076 — jangan sampai seluruh daftar bukti gagal dimuat.
    const kol = 'id,nama,kategori,jumlah,tgl_iso,by_user,bukti';
    type Bar = { id: number; no_dok?: string | null; nama: string; kategori: string | null; jumlah: number; tgl_iso: string; by_user: string | null; bukti: string };
    const halaman = (pilih: string) => (a: number, b: number) => supabase().from('expenses')
      .select(pilih).not('bukti', 'is', null).order('id').range(a, b);
    const cek = await halaman(`${kol},no_dok`)(0, 0);
    const data = (cek.error
      ? await fetchAllPaged<unknown>(halaman(kol))
      : await fetchAllPaged<unknown>(halaman(`${kol},no_dok`))) as Bar[];
    data.forEach((r) => {
      const rupiah = 'Rp ' + r.jumlah.toLocaleString('id-ID');
      m.set(r.bukti, {
        label: r.no_dok || `Beban #${r.id}`,
        detail: `${r.nama}${r.kategori ? ` · ${r.kategori}` : ''} · ${rupiah} · ${r.tgl_iso}${r.by_user ? ` · dicatat ${r.by_user}` : ''}`,
        expenseId: r.id,
        dlName: `${r.no_dok || 'beban-' + r.tgl_iso}-${bersih(r.nama)}.jpg`,
      });
    });
  } else if (bucket === 'absensi') {
    // Foto koreksi = bukti sengketa: harus terlihat SIAPA yang mengajukan dan SIAPA yang
    // memutuskan — tanpa itu fotonya tak bisa dipakai menjawab keberatan karyawan.
    const data = await fetchAllPaged<unknown>((a, b) =>
      supabase().from('attendance').select('tgl,selfie,kategori,kat_override,korr_log,employees(nama,role)').order('id').range(a, b));
    type Korr = { foto?: string | null; action?: string | null; by?: string | null; role?: string | null; ket?: string | null; at?: string | null };
    (data as { tgl: string; selfie: string | null; kategori: string | null; kat_override: string | null; korr_log: Korr[] | null; employees: { nama: string; role?: string } | { nama: string; role?: string }[] | null }[]).forEach((r) => {
      const e = Array.isArray(r.employees) ? r.employees[0] : r.employees;
      const nm = e?.nama ?? 'karyawan';
      const log = r.korr_log ?? [];
      const minta = log.find((k) => /request|ajukan/i.test(k.action ?? ''));
      const putus = [...log].reverse().find((k) => /approve|reject|setuju|tolak|cancel|batal/i.test(k.action ?? ''));
      const jejak = [
        minta?.by ? `diminta ${minta.by}` : '',
        putus?.by ? `${/reject|tolak/i.test(putus.action ?? '') ? 'ditolak' : (/cancel|batal/i.test(putus.action ?? '') ? 'dibatalkan' : 'disetujui')} ${putus.by}${putus.role ? ` (${putus.role})` : ''}` : (minta ? 'menunggu keputusan' : ''),
      ].filter(Boolean).join(' · ');
      const kat = r.kat_override || r.kategori || '';
      if (r.selfie) m.set(r.selfie, { label: `${nm} · ${r.tgl}`, detail: [e?.role, kat, jejak].filter(Boolean).join(' · ') || undefined, dlName: `absen-${r.tgl}-${bersih(nm)}.jpg` });
      log.forEach((k, i) => {
        if (!k.foto) return;
        m.set(k.foto, {
          label: `${nm} · ${r.tgl} (koreksi)`,
          detail: [k.by ? `diajukan ${k.by}` : '', jejak && !k.by ? jejak : (putus?.by && putus.by !== k.by ? `diputus ${putus.by}` : ''), k.ket ?? ''].filter(Boolean).join(' · ') || undefined,
          dlName: `koreksi-${r.tgl}-${bersih(nm)}-${i}.jpg`,
        });
      });
    });
  } else if (bucket === 'dokumen') {
    // Dokumen terkirim tidak punya tabel sendiri — jejaknya di log tiket ("… berkas <path>").
    // Tanpa ini kolomnya hanya menampilkan nama berkas acak, dan petugas tak tahu itu nota,
    // tanda terima, atau surat kuasa milik siapa.
    const data = await fetchAllPaged<{ ticket_no: string; catatan: string | null; at: string; by_user: string | null }>((a, b) =>
      supabase().from('ticket_logs').select('ticket_no,catatan,at,by_user')
        .eq('status', 'Dokumen dikirim').order('at', { ascending: false }).order('id').range(a, b));
    data.forEach((r) => {
      const path = /berkas\s+(\S+)/.exec(r.catatan ?? '')?.[1];
      if (!path) return;
      const jenis = (r.catatan ?? '').split(' dikirim')[0]?.trim() || 'Dokumen';
      m.set(path, {
        ticketNo: r.ticket_no,
        label: `${jenis} · ${r.ticket_no}`,
        detail: `dikirim ${new Date(r.at).toLocaleDateString('id-ID')}${r.by_user ? ` oleh ${r.by_user}` : ''}`,
        dlName: `${bersih(jenis)}-${r.ticket_no}.html`,
      });
    });
  }
  return m;
}

/** Hapus banyak orphan sekali jalan — re-cek referensi SEKALI (bukan per-file), tolak yang tertaut. */
export async function deleteOrphans(bucket: string, paths: string[]): Promise<{ deleted: number; protectedN: number }> {
  const refs = await referencedPaths(bucket);
  const aman = paths.filter((p) => !refs.has(p));
  const protectedN = paths.length - aman.length;
  if (aman.length) {
    const { error } = await supabase().storage.from(bucket).remove(aman);
    if (error) throw new Error(error.message);
  }
  return { deleted: aman.length, protectedN };
}

/** Unduh objek sebagai Blob (untuk a.download ber-nama — window.open tak bisa merename). */
export async function downloadBlob(bucket: string, path: string): Promise<Blob> {
  const { data, error } = await supabase().storage.from(bucket).download(path);
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Auto-hapus SELFIE absensi lebih tua dari `days` hari (kebijakan Apo 20 Jul; hari settable).
 * Yang dihapus HANYA fotonya — baris kehadiran (jam, kategori, log) tetap utuh; kolom selfie
 * di-NULL-kan agar tak ada tautan menggantung. Foto koreksi (korr_log) TIDAK ikut — itu bukti
 * sengketa approval, jumlahnya sedikit; bersihkan manual dari daftar bila perlu.
 */
export async function cleanupSelfies(days: number): Promise<number> {
  if (days <= 0) return 0;
  const cutoff = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
  const { data, error } = await supabase().from('attendance')
    .select('id,selfie').not('selfie', 'is', null).lt('tgl', cutoff).limit(200); // batch 200/kunjungan — cukup & ringan
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as { id: number; selfie: string }[];
  if (!rows.length) return 0;
  const { error: e2 } = await supabase().storage.from('absensi').remove(rows.map((r) => r.selfie));
  if (e2) throw new Error(e2.message);
  const { error: e3 } = await supabase().from('attendance').update({ selfie: null }).in('id', rows.map((r) => r.id));
  if (e3) throw new Error(e3.message);
  return rows.length;
}
